import React from 'react';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'src/app/atoms/Typography/Typography';
import TED from 'src/app/icons/Custom/Logos/TED';
import KonMari from 'src/app/icons/Custom/Logos/KonMari';
import MatthewHussey from 'src/app/icons/Custom/Logos/MatthewHussey';
import MelRobbins from 'src/app/icons/Custom/Logos/MelRobbins';
import BODi from 'src/app/icons/Custom/Logos/BODi';
import Athletic from 'src/app/icons/Custom/Logos/Athletic';
import Mindbody from 'src/app/icons/Custom/Logos/Mindbody';
import Budgetnista from 'src/app/icons/Custom/Logos/Budgetnista';
import MarieForleo from 'src/app/icons/Custom/Logos/MarieForleo';
import Keap from 'src/app/icons/Custom/Logos/Keap';
import MarkHyman from 'src/app/icons/Custom/Logos/MarkHyman';
import TheHomeEdit from 'src/app/icons/Custom/Logos/TheHomeEdit';
import DailyStoic from 'src/app/icons/Custom/Logos/DailyStoic';
import HadarShemesh from 'src/app/icons/Custom/Logos/HadarShemesh';
import Whole30 from 'src/app/icons/Custom/Logos/Whole30';
import BecomingMinimalist from 'src/app/icons/Custom/Logos/BecomingMinimalist';
import JimKwik from 'src/app/icons/Custom/Logos/JimKwik';
import CreateTheLove from 'src/app/icons/Custom/Logos/CreateTheLove';
import Jefferson from 'src/app/icons/Custom/Logos/Jefferson';
import Luvvie from 'src/app/icons/Custom/Logos/Luvvie';
import Cambridge from 'src/app/icons/Custom/Logos/Cambridge';
import { cssVar } from 'src/app/styles/helper';
import { GradientLeft, GradientRight, LogosContainer, LogosRail } from './CompanyLogoSet.styles';

export interface CompanyLogoSetProps {
  title?: TypographyProps;
  theme?: 'default' | 'brand';
  customGradientColor?: string;
}

const COLORS_MAP = {
  default: {
    color: cssVar('brand-950'),
    opacity: 0.5,
  },
  brand: {
    color: cssVar('base-white'),
    opacity: 1,
  },
};

const CompanyLogoSet = ({
  title,
  theme = 'default',
  customGradientColor = '#000000',
}: CompanyLogoSetProps): JSX.Element => {
  const logos = [
    TED,
    KonMari,
    MatthewHussey,
    MelRobbins,
    BODi,
    Athletic,
    Mindbody,
    Budgetnista,
    MarieForleo,
    Keap,
    MarkHyman,
    TheHomeEdit,
    DailyStoic,
    HadarShemesh,
    Whole30,
    BecomingMinimalist,
    JimKwik,
    CreateTheLove,
    Jefferson,
    Luvvie,
    Cambridge,
  ];
  const colors = COLORS_MAP[theme];

  const duplicatedLogos = [...logos, ...logos];

  const logosFirstHalf = [...logos].slice(0, logos.length / 2);
  const logosSecondHalf = [...logos].slice(logos.length / 2);

  const duplicatedLogosFirstHalf = [...logosFirstHalf, ...logosFirstHalf];
  const duplicatedLogosSecondHalf = [...logosSecondHalf, ...logosSecondHalf];

  const renderRow = (logoSet: typeof logos, reverse = false, duration = 60) => (
    <LogosRail
      $reverse={reverse}
      $duration={duration}
    >
      {logoSet.map((Logo, index) => (
        <Logo
          key={index}
          color={colors.color}
          opacity={colors.opacity}
          className='logo'
        />
      ))}
    </LogosRail>
  );

  return (
    <Grid>
      <Stack
        direction='column'
        gap='spacing-4xl'
        overflow='hidden'
        position='relative'
      >
        {title && <Typography {...title} />}
        <LogosContainer
          position='relative'
          hideOn='sm'
        >
          <GradientLeft
            theme={theme}
            customGradientColor={customGradientColor}
          />
          <GradientRight
            theme={theme}
            customGradientColor={customGradientColor}
          />
          {renderRow(duplicatedLogos)}
        </LogosContainer>

        <Grid
          showOn='sm'
          gap='spacing-xl'
          gapSm='spacing-4xl'
          totalColumns={1}
        >
          <GradientLeft
            theme={theme}
            customGradientColor={customGradientColor}
          />
          <GradientRight
            theme={theme}
            customGradientColor={customGradientColor}
          />
          <LogosContainer position='relative'>{renderRow(duplicatedLogosFirstHalf, false, 35)}</LogosContainer>
          <LogosContainer position='relative'>{renderRow(duplicatedLogosSecondHalf, true, 35)}</LogosContainer>
        </Grid>
      </Stack>
    </Grid>
  );
};

export default CompanyLogoSet;
